import { useEffect, useState } from 'react';
import { CONTENT_TYPE_FIELD, CpsFacetValue, DOC_ID_FIELD, EXTENSION_FIELD, PERMISSIONS_FIELD } from '@cs-cms/types';
import { useDocumentsContext, useSearchContext } from '@cs-cms/state-management';
import { getActionsFromFlag } from '../helpers/cpsGridHelpers';
import { computeActionsFacets } from '../helpers/cpsGridActionsFacets';
import { CpsGridDocument } from '../cpsGridTypes';

interface UseCpsGridDocumentsParams {
  selectedIndexId: number | undefined;
  selectedView: number | undefined;
  translate: (key: string) => string;
}

interface UseCpsGridDocumentsResult {
  gridData: CpsGridDocument[];
  initialFacets: Record<string, CpsFacetValue[]> | undefined;
  actionsFacets: CpsFacetValue[];
}

export const useCpsGridDocuments = (params: UseCpsGridDocumentsParams): UseCpsGridDocumentsResult => {
  const { selectedIndexId, selectedView, translate } = params;
  const { data } = useSearchContext();
  const { setSelectedDocuments } = useDocumentsContext();
  const [gridData, setGridData] = useState<CpsGridDocument[]>([]);
  const [initialFacets, setInitialFacets] = useState<Record<string, CpsFacetValue[]>>();

  useEffect(() => {
    if (!data?.documentsWithPerms) {
      return;
    }

    const result: CpsGridDocument[] = data.documentsWithPerms.map(item => ({
      [DOC_ID_FIELD]: item.document[DOC_ID_FIELD] as string,
      [CONTENT_TYPE_FIELD]: item.document[CONTENT_TYPE_FIELD] as string,
      [EXTENSION_FIELD]: item.document[EXTENSION_FIELD] as string,
      ...item.document,
      [PERMISSIONS_FIELD]: getActionsFromFlag(item.actions)
    }));
    setGridData(result);

    if (data.facets && !initialFacets) {
      setInitialFacets(data.facets);
    }
  }, [data?.documentsWithPerms, data?.facets, initialFacets]);

  useEffect(() => {
    if (setSelectedDocuments) {
      setSelectedDocuments([]);
    }
    setInitialFacets(undefined);
  }, [selectedIndexId, selectedView, setSelectedDocuments]);

  const actionsFacets = computeActionsFacets(gridData, translate);

  return { gridData, initialFacets, actionsFacets };
};
